import { I18n } from 'react-redux-i18n'
import {
  MT4Connector,
  Trade,
  Price,
  TradeObject,
  calculatePl,
  // calcTpValue,
  // calcSlValue,
  // calcPendingPriceValue,
} from '@bdswiss/mt4-connector'

import { IChartWidgetApi, IPositionLineAdapter } from '../types/charting_library'

import { store } from '../../../index'
import {
  formatProfitLoss,
  formatCurrency,
  formatPriceDifference,
} from '../../../lib/currency-formatter'
// import {
//   inputPositionSL,
//   inputPositionTP,
//   inputPositionPrice,
//   setPrice,
//   setTakeProfit,
//   setStopLoss,
// } from '../../../actions/TradeActions'
import { /* TradingAction, changeSymbol,  */ selectPosition } from '../../../actions'

const mt4 = MT4Connector.Instance

const chartShapeFont = 'bold 10pt Verdana'

export interface PositionShape {
  order: number
  expanded: boolean
  pLine?: IPositionLineAdapter
  slLine?: IPositionLineAdapter
  tpLine?: IPositionLineAdapter
}
export interface PositionsShapes {
  [order: string]: PositionShape
}

export interface IChartPositions {
  initChart(chart: IChartWidgetApi): void
  displayPositions(): void
  clearPositions(): void
}

export class ChartPositions implements IChartPositions {
  private chart!: IChartWidgetApi
  private positionsShapes: PositionsShapes = {}

  // constructor() {}
  public initChart(chart: IChartWidgetApi) {
    this.chart = chart
  }

  public clearPositions() {
    const lines = Object.keys(this.positionsShapes)
    if (lines.length) {
      lines.forEach(line => this._erasePositionShape(this.positionsShapes[line]))
      this.positionsShapes = {}
    }
  }

  public displayPositions() {
    const {
      appState: { symbol },
      appState: { chartMode },
      tradingPane,
    } = store.getState()
    const positions = Array.from(mt4.positions.values())
    const pendingOrders = Array.from(mt4.pendingOrders.values())

    let currentPositions: TradeObject[] = []
    switch (chartMode) {
      case 'open':
        currentPositions = positions.filter(position => position.symbol === symbol)
        break
      case 'pending':
        currentPositions = pendingOrders.filter(position => position.symbol === symbol)
        break
      case 'trade':
        const tradeType = tradingPane.tradeType
        const {
          price,
          pendingOrderEnabled,
          priceInitial,
          profit,
          loss,
          stopLoss,
          takeProfit,
          stopLossEnabled,
          takeProfitEnabled,
          takeProfitInitial,
          stopLossInitial,
        } = tradingPane[tradeType]
        currentPositions = [
          {
            closePrice: 0,
            closeTime: '',
            type: tradeType + (pendingOrderEnabled ? ' limit' : ''),
            commission: stopLossEnabled ? loss || stopLossInitial : 0,
            digits: 0,
            login: 0,
            openPrice: pendingOrderEnabled ? price || priceInitial : 0,
            openTime: '',
            profit,
            sl: stopLossEnabled ? stopLoss || stopLossInitial : 0,
            swaps: 0,
            symbol,
            order: -1,
            tp: takeProfitEnabled ? takeProfit || takeProfitInitial : 0,
            volume: tradingPane.lots,
            convRate1: 1,
            magic: 0,
            profitPercent: 0,
            tpInitial: takeProfitInitial,
            slInitial: stopLossInitial,
            currentPrice: 0,
            rate: 1,
          } as TradeObject,
        ]
        break
    }
    const shapes = Object.keys(this.positionsShapes)
    if (currentPositions.length || shapes.length) {
      try {
        // cleanup positions shapes
        const remove = shapes.filter(
          shape =>
            !currentPositions.find(position => this.positionsShapes[shape].order === position.order)
        )
        if (remove.length) {
          remove.forEach(shape => {
            this._erasePositionShape(this.positionsShapes[shape])
            delete this.positionsShapes[shape]
          })
        }
        // display positions shapes
        currentPositions.forEach(position => this._createPositionShape(position))
      } catch (e) {
        console.log(`Error displaying positions on chart: ${e.message}`)
      }
    }
  }

  private _createPositionShape(position: Trade) {
    const {
      appState: { chartMode, selectedPosition, selectedOrder, positionExpanded, orderExpanded },
    } = store.getState()
    const positionShape = this.positionsShapes[position.order]
    const { selected, expanded } =
      chartMode === 'open'
        ? { selected: selectedPosition, expanded: positionExpanded }
        : chartMode === 'pending'
        ? { selected: selectedOrder, expanded: orderExpanded }
        : { selected: false, expanded: false }
    if (positionShape) {
      if (!expanded || !selected || (expanded && position.order === selected)) {
        this._drawPositionLine(position, positionShape)
      } else {
        this._erasePositionShape(positionShape)
      }
    } else {
      if (!expanded || !selected || (expanded && position.order === selected)) {
        const newPositionShape = (this.positionsShapes[position.order] = {
          order: position.order,
          expanded: false,
        })
        this._drawPositionLine(position, newPositionShape)
      } else {
        this.positionsShapes[position.order] = { order: position.order, expanded: false }
      }
    }
  }

  private _drawPositionLine(position: Trade, positionShape: PositionShape) {
    const {
      appConfig: { locale },
      appState: {
        chartMode,
        currency,
        symbol,
        selectedPosition,
        positionExpanded,
        selectedOrder,
        orderExpanded,
      },
      positionsInputs,
    } = store.getState()
    // const onInputPrice = (order: number, value: number) => store.dispatch(inputPositionPrice(order, value))
    // const onSetPrice = (value: number) => store.dispatch(setPrice(value))
    const assets = mt4.assets
    const asset = assets[symbol]
    if (chartMode === 'trade') {
      // Trading pane price line
      const { type, openPrice } = position
      if (openPrice <= 0) {
        if (positionShape.pLine) {
          positionShape.pLine.remove()
          delete positionShape.pLine
        }
        this._drawPositionDetails(position, positionShape)
        return
      }
      // const pLine = positionShape.pLine ? positionShape.pLine : this.chart.createOrderLine()
      const pLine = positionShape.pLine ? positionShape.pLine : this.chart.createPositionLine({})
      let priceDifference
      let differencePercent
      if (/buy/i.test(type)) {
        priceDifference = openPrice - asset.mask
        differencePercent = (priceDifference * 100) / asset.mask
      } else {
        priceDifference = openPrice - asset.mbid
        differencePercent = (priceDifference * 100) / asset.mbid
      }
      const formattedDifference = formatPriceDifference(
        priceDifference,
        asset.digits,
        asset.currency,
        priceDifference! >= 0
      )
      const formattedPercent =
        ' (' + (priceDifference! >= 0 ? '+' : '') + differencePercent.toFixed(2) + '%)'
      const label = I18n.t(/buy/i.test(type) ? 'buyPrice' : 'sellPrice')
      const typeColor = priceDifference > 0 ? '#67AD5B' : '#E15241'
      if (!positionShape.pLine) {
        pLine
          .setQuantityFont(chartShapeFont)
          .setQuantityTextColor('#FFF')
          .setQuantity(formattedDifference)
          .setLineStyle(0)
          .setLineWidth(2)
          .setPrice(+openPrice.toFixed(asset.digits))
          // .setText('\u2195')
          .setBodyFont(chartShapeFont)
          .setLineStyle(0)
          .setLineWidth(2)
        positionShape.pLine = pLine
      }
      pLine
        .setBodyTextColor(typeColor)
        .setText(label)
        .setBodyBorderColor(typeColor)
        .setQuantityBackgroundColor(typeColor)
        .setQuantityBorderColor(typeColor)
        .setLineColor(typeColor)
        .setPrice(+openPrice.toFixed(asset.digits))
        .setTooltip(`${formattedDifference} ${formattedPercent}\n ${I18n.t('priceDistance')}`)
        .setQuantity(formattedDifference)
      // .onMove(function(this: any) {
      //   onSetPrice(
      //     calcPendingPriceValue(/buy/i.test(type) ? 'buy' : 'sell', asset, this.getPrice())
      //   )
      // })
      this._drawPositionDetails(position, positionShape)
    } else if (chartMode === 'pending') {
      // Pending position price line
      const { profit, type, investment, leverage, volume, order, openPrice } = position
      const { priceInput } = positionsInputs[position.order] || {}
      const profitColor = profit > 0 ? '#67AD5B' : '#E15241'
      const invested =
        investment && leverage
          ? `${formatCurrency(investment, currency, locale)} (x${leverage})`
          : `${volume} ${I18n.t('lots')}`
      const price = priceInput || openPrice
      let pLine: any = {}
      if (order === selectedOrder && orderExpanded) {
        if (
          positionShape.pLine &&
          (positionShape.pLine as any)._line.toolname !== 'LineToolOrder'
        ) {
          positionShape.pLine.remove()
          delete positionShape.pLine
        }
        // pLine = positionShape.pLine ? positionShape.pLine : this.chart.createOrderLine()
        pLine = positionShape.pLine ? positionShape.pLine : this.chart.createPositionLine({})
        if (!positionShape.pLine) {
          const typeColor = /buy/i.test(type) ? '#67AD5B' : '#E15241'
          pLine
            .setBodyTextColor(typeColor)
            .setBodyBorderColor(typeColor)
            .setQuantityFont(chartShapeFont)
            .setQuantityTextColor('#FFF')
            .setQuantityBackgroundColor(typeColor)
            .setQuantityBorderColor(typeColor)
            .setQuantity(price.toFixed(asset.digits))
            .setLineColor(typeColor)
            .setLineStyle(0)
            .setLineWidth(2)
            .setPrice(price.toFixed(asset.digits))
            .setText('\u2195')
            .setBodyFont(chartShapeFont)
            .setLineStyle(0)
            .setLineWidth(2)
          positionShape.pLine = pLine
        }
        pLine
          .setPrice(price.toFixed(asset.digits))
          .setTooltip(`${I18n.t('openPrice')}\n ${price.toFixed(asset.digits)}`)
          .setText(I18n.t('openPrice'))
        // .onMove(function(this: any) {
        //   onInputPrice(
        //     order,
        //     calcPendingPriceValue(/buy/i.test(type) ? 'buy' : 'sell', asset, this.getPrice())
        //   )
        // })
      } else {
        if (
          positionShape.pLine &&
          (positionShape.pLine as any)._line.toolname !== 'LineToolPosition'
        ) {
          positionShape.pLine.remove()
          delete positionShape.pLine
        }
        pLine = positionShape.pLine ? positionShape.pLine : this.chart.createPositionLine({})
        if (!positionShape.pLine) {
          const typeColor = /buy/i.test(type) ? '#67AD5B' : '#E15241'
          pLine
            .setBodyFont(chartShapeFont)
            .setQuantityTextColor('#FFF')
            .setQuantityFont(chartShapeFont)
            .setQuantityBackgroundColor(typeColor)
            .setQuantity(invested)
            .setPrice(openPrice)
          positionShape.pLine = pLine
        }
        pLine
          .setBodyTextColor(profitColor)
          .setText(openPrice.toFixed(asset.digits))
          .setTooltip(
            `${I18n.t(type)}\n ${I18n.t('order')} #${order}\n ${I18n.t(
              'price'
            )} ${openPrice.toFixed(asset.digits)}\n ${I18n.t('investment')} ${invested}`
          )
          .onModify(() => this._selectPositionLine(positionShape))
      }
      if (order === selectedOrder) {
        pLine.setLineStyle(0)
        if (orderExpanded) {
          pLine.setLineWidth(2)
          this._drawPositionDetails(position, positionShape)
        } else {
          pLine.setLineWidth(1)
          this._erasePositionShape(positionShape, true)
        }
      } else {
        pLine.setLineStyle(2)
        pLine.setLineWidth(1)
        this._erasePositionShape(positionShape, true)
      }
    } else {
      // Open position price line
      const pLine = positionShape.pLine ? positionShape.pLine : this.chart.createPositionLine({})
      const { profit, type, investment, leverage, volume, order, openPrice } = position
      const profitColor = profit > 0 ? '#67AD5B' : '#E15241'
      const invested =
        investment && leverage
          ? `${formatCurrency(investment, currency, locale)} (x${leverage})`
          : `${volume} ${I18n.t('lots')}`
      const profitLoss = formatProfitLoss(profit, currency, locale)
      const percent = investment
        ? `(${(profit > 0 ? '+' : '') + ((+profit.toFixed(2) * 100) / investment).toFixed(2)}%)`
        : ''
      if (!positionShape.pLine) {
        const typeColor = /buy/i.test(type) ? '#67AD5B' : '#E15241'
        pLine
          .setBodyFont(chartShapeFont)
          .setQuantityTextColor('#FFF')
          .setQuantityFont(chartShapeFont)
          .setQuantityBackgroundColor(typeColor)
          .setQuantity(invested)
          .setPrice(openPrice)
          .onModify(() => this._selectPositionLine(positionShape))
        positionShape.pLine = pLine
      }
      pLine
        .setBodyTextColor(profitColor)
        .setText(profitLoss)
        .setTooltip(
          `${I18n.t(type)}\n ${I18n.t('order')} #${order}\n ${I18n.t(
            'investment'
          )} ${invested}\n ${I18n.t('profit')}/${I18n.t('loss')} ${profitLoss} ${percent}`
        )
      if (order === selectedPosition) {
        pLine.setLineStyle(0)
        if (positionExpanded) {
          pLine.setLineWidth(2)
          this._drawPositionDetails(position, positionShape)
        } else {
          pLine.setLineWidth(1)
          this._erasePositionShape(positionShape, true)
        }
      } else {
        pLine.setLineStyle(2)
        pLine.setLineWidth(1)
        this._erasePositionShape(positionShape, true)
      }
    }
  }

  private _drawPositionDetails(position: Trade, positionShape: PositionShape) {
    const { positionsInputs } = store.getState()
    const { slInput, tpInput } = positionsInputs[position.order] || {}
    const { sl, tp } = position
    if (slInput !== undefined || sl) {
      this._drawSLTPLine(position, positionShape, 'slLine')
    } else if (positionShape.slLine) {
      positionShape.slLine.remove()
      delete positionShape.slLine
    }
    if (tpInput !== undefined || tp) {
      this._drawSLTPLine(position, positionShape, 'tpLine')
    } else if (positionShape.tpLine) {
      positionShape.tpLine.remove()
      delete positionShape.tpLine
    }
    positionShape.expanded = true
  }

  private _drawSLTPLine(position: Trade, positionShape: PositionShape, type: 'slLine' | 'tpLine') {
    const {
      appConfig: { locale },
      appState: { chartMode, currency },
      positionsInputs,
    } = store.getState()
    const assets = mt4.assets
    // const onInputStopLoss = (order: number, value: number) => store.dispatch(inputPositionSL(order, value))
    // const onInputTakePorfit = (order: number, value: number) => store.dispatch(inputPositionTP(order, value))
    const { tp, sl, profit, commission, rate, symbol /* , order, openPrice */ } = position
    const { slInput, tpInput /* , priceInput */ } = positionsInputs[position.order] || {}
    const { typeColor, sltp, label } =
      type === 'tpLine'
        ? {
            typeColor: '#67AD5B',
            sltp: tpInput !== undefined ? tpInput : tp,
            label: I18n.t('takeProfit'),
          }
        : {
            typeColor: '#E15241',
            sltp: slInput !== undefined ? slInput : sl,
            label: I18n.t('stopLoss'),
          }
    const asset = assets[symbol]
    let pl = NaN
    if (chartMode === 'open') {
      pl = asset
        ? /buy/i.test(position.type)
          ? calculatePl({ ...(asset as Price), mbid: sltp }, position, rate)
          : calculatePl({ ...(asset as Price), mask: sltp }, position, rate)
        : NaN
    } else if (chartMode === 'trade') {
      pl = type === 'tpLine' ? profit : commission
    }
    const sltpPL = isNaN(pl) ? '' : formatProfitLoss(pl, currency, locale)
    const tooltip = `${label} \n${I18n.t('price')} ${sltp} \n${
      type === 'tpLine' ? I18n.t('profit') : I18n.t('loss')
    } ${sltpPL}`
    if (!positionShape[type]) {
      // positionShape[type] = this.chart.createPositionLine()
      positionShape[type] = this.chart
        // .createOrderLine()
        .createPositionLine({})
        .setBodyTextColor(typeColor)
        .setBodyBorderColor(typeColor)
        .setQuantityFont(chartShapeFont)
        .setQuantityTextColor('#FFF')
        .setQuantityBackgroundColor(typeColor)
        .setQuantityBorderColor(typeColor)
        // .setQuantity(`${label} ${sltpPL}`)
        .setQuantity(sltpPL)
        .setLineColor(typeColor)
        .setLineStyle(0)
        .setLineWidth(2)
        .setPrice(sltp)
        // .setText('\u2195')
        .setText(label)
        .setBodyFont(chartShapeFont)
        .setTooltip(tooltip)
    } else {
      // if (type === 'tpLine') {
      //   positionShape[type].onMove(function(this: any) {
      //     // prettier-ignore
      //     const price = chartMode === 'pending'
      //       ? priceInput || openPrice
      //       : chartMode === 'open'
      //         ? /buy/i.test(position.type)
      //           ? Math.max(position.openPrice, asset.mbid)
      //           : Math.min(position.openPrice, asset.mask)
      //         : /limit/i.test(position.type)
      //           ? priceInput
      //           : undefined
      //     const value = calcTpValue(
      //       position,
      //       asset,
      //       this.getPrice().toFixed(asset.digits),
      //       price,
      //       chartMode === 'pending'
      //     )
      //     onInputTakePorfit(order, value)
      //   })
      // } else {
      //   positionShape[type].onMove(function(this: any) {
      //     // prettier-ignore
      //     const price = chartMode === 'pending'
      //       ? priceInput || openPrice
      //       : chartMode === 'open'
      //         ? undefined
      //         : /limit/i.test(position.type)
      //           ? priceInput
      //           : undefined
      //     const value = calcSlValue(
      //       position,
      //       asset,
      //       this.getPrice().toFixed(asset.digits),
      //       price,
      //       chartMode === 'pending'
      //     )
      //     onInputStopLoss(order, value)
      //   })
      // }
      const line = positionShape[type]!
      // const oldPl = line.getQuantity().split(label)[1]
      // if (line.getPrice() !== sltp || oldPl !== sltpPL) {
      line
        .setPrice(sltp)
        // .setQuantity(`${label} ${sltpPL}`)
        .setQuantity(sltpPL)
        .setTooltip(tooltip)
      // }
    }
  }

  private _erasePositionShape(positionShape: PositionShape, detailsOnly?: boolean) {
    const {
      appState: { positionsOpened, selectedPosition },
    } = store.getState()
    if (positionsOpened && positionShape.order === selectedPosition) {
      positionShape.expanded = false
    }
    Object.keys(positionShape)
      .filter(key => /Line/.test(key))
      .filter(key => !detailsOnly || (detailsOnly && key !== 'pLine'))
      .forEach(line => {
        ;(positionShape as any)[line].remove()
        delete (positionShape as any)[line]
      })
  }

  private _selectPositionLine(positionShape: PositionShape) {
    const {
      appState: { chartMode, selectedPosition, selectedOrder },
    } = store.getState()
    const onSelectPosition = (order: number, expanded?: boolean) =>
      store.dispatch(selectPosition(order, expanded))
    // prettier-ignore
    const order =
      chartMode === 'open'
        ? selectedPosition !== positionShape.order
          ? positionShape.order
          : NaN
        : chartMode === 'pending'
          ? selectedOrder !== positionShape.order
            ? positionShape.order
            : NaN
          : NaN
    if (isNaN(order)) {
      onSelectPosition(order, false)
    } else {
      onSelectPosition(order, true)
    }
  }
}
